import { Button, Modal, Tooltip, Icon, Empty, Input, Checkbox, Popconfirm, Badge, message } from "antd";
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import styled from "styled-components";
import uid from "uid";

import { department } from "~/components/RegisterTasks/Utils";
import * as checklistsActions from "~/containers/Checklist/actions";
import CollapsibleSection from "../components/CollapsibleSection";

var DEPT_ORDER = [1, 2, 3, 4, 5, 6];
var DEPT_COLORS = { 1: '#722ed1', 2: '#1890ff', 3: '#13c2c2', 4: '#52c41a', 5: '#fa8c16', 6: '#eb2f96' };
var DEPT_BG = { 1: '#f9f0ff', 2: '#f0f5ff', 3: '#e6fffb', 4: '#f6ffed', 5: '#fff7e6', 6: '#fff0f6' };
var DEPT_ICONS = { 1: 'audit', 2: 'solution', 3: 'calculator', 4: 'file-protect', 5: 'team', 6: 'customer-service' };

var TaskItem = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 14px;
  background: ${function(p) { return p.isModified ? '#fffbe6' : '#fff'; }};
  border: 1px solid ${function(p) { return p.isDragging ? '#1890ff' : p.isEditing ? '#1890ff' : p.isModified ? '#ffe58f' : '#f0f0f0'; }};
  border-radius: 6px;
  margin-bottom: 6px;
  transition: border-color 0.15s, box-shadow 0.15s;
  gap: 10px;
  box-shadow: ${function(p) { return p.isDragging ? '0 4px 12px rgba(24,144,255,0.15)' : 'none'; }};
  &:hover { border-color: #d9d9d9; }
`;

var DragHandle = styled.div`
  color: #d9d9d9;
  cursor: grab;
  display: flex;
  align-items: center;
  flex-shrink: 0;
  &:hover { color: #8c8c8c; }
`;

var TaskName = styled.div`
  flex: 1;
  font-size: 13px;
  color: #262626;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
  &:hover { color: #1890ff; }
`;

var TaskActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  opacity: 0;
  transition: opacity 0.15s;
  ${TaskItem}:hover & { opacity: 1; }
`;

var QuickAddRow = styled.div`
  display: flex;
  align-items: center;
  padding: 10px 14px;
  gap: 10px;
  margin-top: 6px;
  border: 1px dashed #d9d9d9;
  border-radius: 8px;
  background: #fafafa;
  cursor: text;
  transition: all 0.15s;
  &:hover, &:focus-within {
    border-color: #1890ff;
    background: #fff;
  }
  .ant-input {
    border: none;
    border-radius: 0;
    font-size: 13px;
    background: transparent;
    box-shadow: none;
    padding: 0;
    height: 28px;
    &:focus {
      box-shadow: none;
    }
  }
`;

var TaskCount = styled.span`
  font-size: 12px;
  color: #8c8c8c;
  background: #f5f5f5;
  padding: 1px 8px;
  border-radius: 10px;
  margin-left: 8px;
`;

// Linha de adicionar tarefa: input + botão explícito (além do Enter), pois
// nem todo mundo sabe que dá pra adicionar só com Enter.
var QuickAddTask = function(props) {
  var state = useState("");
  var value = state[0];
  var setValue = state[1];

  var submit = function() {
    var val = value && value.trim();
    if (!val) {
      return;
    }
    props.onAdd(val);
    setValue("");
  };

  var hasValue = !!(value && value.trim());

  return React.createElement(QuickAddRow, null,
    React.createElement(Icon, { type: 'plus', style: { color: '#bfbfbf', fontSize: 12, flexShrink: 0 } }),
    React.createElement(Input, {
      className: 'checklist-quick-add',
      placeholder: 'Adicionar tarefa...',
      size: 'small',
      value: value,
      onChange: function(e) { setValue(e.target.value); },
      onPressEnter: submit
    }),
    React.createElement(Button, {
      type: 'primary',
      size: 'small',
      icon: 'plus',
      disabled: !hasValue,
      onClick: submit,
      style: { flexShrink: 0 }
    }, 'Adicionar')
  );
};

// SaveBar is now a fixed floating bar (see render below)

export var ChecklistTasks = function(props) {
  var initialStatus = props.initialStatus;
  var addTrigger = props.addTrigger;
  var searchTerm = props.searchTerm || '';
  var dispatch = useDispatch();
  var checklists = useSelector(function(state) { return state.checklists; });
  var selectedStatusState = useState(initialStatus || 2);
  var selectedStatus = selectedStatusState[0];
  var setSelectedStatus = selectedStatusState[1];

  var editingIdState = useState(null);
  var editingId = editingIdState[0];
  var setEditingId = editingIdState[1];
  var editValueState = useState('');
  var editValue = editValueState[0];
  var setEditValue = editValueState[1];

  var selectedState = useState({});
  var selectedMap = selectedState[0];
  var setSelectedMap = selectedState[1];

  // Local tasks state for tracking unsaved changes
  var localTasksState = useState([]);
  var localTasks = localTasksState[0];
  var setLocalTasks = localTasksState[1];

  var isSavingState = useState(false);
  var isSaving = isSavingState[0];
  var setIsSaving = isSavingState[1];

  // Sync from redux when it changes
  useEffect(function() {
    setLocalTasks(checklists.tasks || []);
  }, [checklists.tasks]);

  useEffect(function() {
    if (initialStatus) setSelectedStatus(initialStatus);
  }, [initialStatus]);

  var prevTriggerRef = React.useRef(addTrigger);
  useEffect(function() {
    if (addTrigger > 0 && addTrigger !== prevTriggerRef.current) {
      var el = document.querySelector('.checklist-quick-add');
      if (el) el.focus();
    }
    prevTriggerRef.current = addTrigger;
  }, [addTrigger]);

  // Count unsaved changes
  var modifiedTasks = localTasks.filter(function(t) {
    return t.is_new || t.is_modified || t.is_deleted;
  });
  var unsavedCount = modifiedTasks.length;

  function createTask(name, deptId) {
    var newTask = {
      id: uid(),
      name: name,
      department: deptId,
      status: selectedStatus,
      is_new: true,
    };
    setLocalTasks(localTasks.concat([newTask]));
  }

  function updateTaskName(task, newName) {
    if (newName && newName.trim() && newName.trim() !== task.name) {
      setLocalTasks(localTasks.map(function(t) {
        if (t.id !== task.id) return t;
        return Object.assign({}, t, { name: newName.trim(), is_modified: !t.is_new });
      }));
    }
    setEditingId(null);
  }

  function deleteTaskLocal(record) {
    if (record.is_new) {
      // Not saved yet — just remove from local
      setLocalTasks(localTasks.filter(function(t) { return t.id !== record.id; }));
    } else {
      setLocalTasks(localTasks.map(function(t) {
        if (t.id !== record.id) return t;
        return Object.assign({}, t, { is_deleted: true });
      }));
    }
  }

  function saveAll() {
    if (modifiedTasks.length === 0) return;
    setIsSaving(true);
    dispatch(checklistsActions.saveChecklistTasks(modifiedTasks))
      .then(function() {
        message.success(modifiedTasks.length + ' alteração' + (modifiedTasks.length > 1 ? 'ões' : '') + ' salva' + (modifiedTasks.length > 1 ? 's' : ''));
      })
      .finally(function() {
        setIsSaving(false);
      });
  }

  function discardAll() {
    setLocalTasks(checklists.tasks || []);
    message.info('Alterações descartadas');
  }

  function startEdit(task) {
    setEditingId(task.id);
    setEditValue(task.name);
  }

  var toggleSelect = function(id, e) {
    if (e) e.stopPropagation();
    var next = Object.assign({}, selectedMap);
    next[id] = !next[id];
    setSelectedMap(next);
  };

  var selectedIds = Object.keys(selectedMap).filter(function(k) { return selectedMap[k]; });
  var selectedCount = selectedIds.length;

  var handleBulkDelete = function() {
    var items = filtered.filter(function(t) { return selectedMap[t.id]; });
    if (items.length === 0) return;
    items.forEach(function(t) { deleteTaskLocal(t); });
    setSelectedMap({});
  };

  // Filter by status (exclude deleted)
  var filtered = localTasks.filter(function(task) {
    return task.status === selectedStatus && !task.is_deleted;
  });
  if (searchTerm && searchTerm.trim()) {
    var term = searchTerm.toLowerCase().trim();
    filtered = filtered.filter(function(t) {
      return (t.name || '').toLowerCase().indexOf(term) !== -1;
    });
  }

  // Group by department
  var grouped = {};
  DEPT_ORDER.forEach(function(d) { grouped[d] = []; });
  filtered.forEach(function(task) {
    var dept = task.department || 1;
    if (!grouped[dept]) grouped[dept] = [];
    grouped[dept].push(task);
  });
  DEPT_ORDER.forEach(function(d) {
    grouped[d].sort(function(a, b) {
      // Use sort_order if set (from drag), otherwise alphabetical
      var aOrder = a.sort_order !== undefined ? a.sort_order : 9999;
      var bOrder = b.sort_order !== undefined ? b.sort_order : 9999;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return (a.name || '').localeCompare(b.name || '');
    });
  });

  var handleDragEnd = function(result) {
    if (!result.destination) return;
    if (result.source.index === result.destination.index) return;

    var deptId = parseInt(result.source.droppableId);
    var deptTasks = (grouped[deptId] || []).slice();

    var removed = deptTasks.splice(result.source.index, 1);
    deptTasks.splice(result.destination.index, 0, removed[0]);

    // Rebuild localTasks with the new order for this department
    // Keep tasks from other departments/statuses untouched
    var reorderedIds = deptTasks.map(function(t) { return t.id; });
    var otherTasks = localTasks.filter(function(t) {
      return !(t.status === selectedStatus && t.department === deptId && !t.is_deleted);
    });

    // Mark reordered tasks as modified so they appear in unsaved count
    var reorderedTasks = deptTasks.map(function(t, i) {
      return Object.assign({}, t, { sort_order: i, is_modified: !t.is_new });
    });

    setLocalTasks(otherTasks.concat(reorderedTasks));
  };

  var visibleDepts = DEPT_ORDER.filter(function(d) { return grouped[d].length > 0; });
  if (filtered.length === 0 && !searchTerm) {
    visibleDepts = DEPT_ORDER;
  }

  return React.createElement('div', null,
    React.createElement(DragDropContext, { onDragEnd: handleDragEnd },
      visibleDepts.map(function(deptId) {
        var deptTasks = grouped[deptId] || [];
        var deptName = department(deptId);
        var color = DEPT_COLORS[deptId] || '#1890ff';
        var bg = DEPT_BG[deptId] || '#f0f5ff';
        var icon = DEPT_ICONS[deptId] || 'folder';

        return React.createElement(CollapsibleSection, {
          key: deptId,
          defaultOpen: false,
          title: React.createElement('span', null,
            deptName,
            React.createElement(TaskCount, null, deptTasks.length)
          ),
          icon: React.createElement(Icon, { type: icon }),
          bg: bg,
          color: color
        },
          React.createElement(Droppable, { droppableId: String(deptId) }, function(provided) {
            return React.createElement('div', Object.assign({ ref: provided.innerRef }, provided.droppableProps),
              deptTasks.map(function(task, index) {
                var isEditing = editingId === task.id;
                var isModified = task.is_new || task.is_modified;

                return React.createElement(Draggable, {
                  key: task.id,
                  draggableId: String(task.id),
                  index: index
                }, function(dragProvided, snapshot) {
                  return React.createElement(TaskItem, Object.assign({
                    ref: dragProvided.innerRef,
                    isDragging: snapshot.isDragging,
                    isEditing: isEditing,
                    isModified: isModified,
                  }, dragProvided.draggableProps),
                    React.createElement('span', {
                      onClick: function(e) { e.stopPropagation(); }
                    },
                      React.createElement(Checkbox, {
                        checked: !!selectedMap[task.id],
                        onChange: function(e) { toggleSelect(task.id, e); }
                      })
                    ),
                    React.createElement(DragHandle, dragProvided.dragHandleProps,
                      React.createElement(Icon, { type: 'menu', style: { fontSize: 12 } })
                    ),
                    React.createElement('span', {
                      style: {
                        width: 22, height: 22, borderRadius: '50%',
                        background: color, color: '#fff',
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 11, fontWeight: 600, flexShrink: 0
                      }
                    }, index + 1),
                    isEditing ? (
                      React.createElement(Input, {
                        autoFocus: true,
                        size: 'small',
                        value: editValue,
                        onChange: function(e) { setEditValue(e.target.value); },
                        onPressEnter: function() { updateTaskName(task, editValue); },
                        onBlur: function() { updateTaskName(task, editValue); },
                        style: { flex: 1 }
                      })
                    ) : (
                      React.createElement(TaskName, {
                        onClick: function() { startEdit(task); }
                      }, task.name)
                    ),
                    !isEditing && React.createElement(TaskActions, null,
                      React.createElement(Tooltip, { title: 'Editar' },
                        React.createElement(Icon, {
                          type: 'edit',
                          style: { cursor: 'pointer', color: '#8c8c8c', fontSize: 14 },
                          onClick: function() { startEdit(task); }
                        })
                      ),
                      React.createElement(Popconfirm, {
                        title: 'Excluir esta tarefa?',
                        okText: 'Sim',
                        cancelText: 'Não',
                        onConfirm: function() { deleteTaskLocal(task); },
                        zIndex: 1020
                      },
                        React.createElement(Tooltip, { title: 'Excluir' },
                          React.createElement(Icon, {
                            type: 'delete',
                            style: { cursor: 'pointer', color: '#ff4d4f', fontSize: 14 }
                          })
                        )
                      )
                    )
                  );
                });
              }),
              provided.placeholder,
              React.createElement(QuickAddTask, {
                key: '_quickadd',
                onAdd: function(name) { createTask(name, deptId); }
              })
            );
          })
        );
      })
    ),
    // Fixed footer bar
    React.createElement('div', {
      style: {
        position: 'fixed',
        bottom: selectedCount > 0 ? 68 : 0,
        right: 0,
        width: 1100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 12,
        padding: '12px 24px',
        background: '#fff',
        borderTop: '1px solid #f0f0f0',
        zIndex: 1020,
      }
    },
      unsavedCount > 0 && React.createElement(Icon, { type: 'warning', theme: 'filled', style: { color: '#faad14', fontSize: 16 } }),
      unsavedCount > 0 && React.createElement('span', { style: { fontSize: 13, color: '#595959', fontWeight: 500 } },
        'Alterações pendentes'
      ),
      unsavedCount > 0 && React.createElement(Button, { onClick: discardAll, style: { height: 44, fontSize: 15, padding: '0 24px', borderRadius: 8 } }, 'Descartar'),
      React.createElement(Button, { type: 'primary', icon: 'save', loading: isSaving, onClick: saveAll, disabled: unsavedCount === 0, style: { height: 44, fontSize: 15, padding: '0 32px', borderRadius: 8 } }, 'Salvar')
    ),
    // Floating bottom toolbar for bulk
    selectedCount > 0 && React.createElement('div', {
      style: {
        position: 'fixed',
        bottom: 24,
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        padding: '10px 20px',
        background: '#262626',
        borderRadius: 8,
        boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
        zIndex: 1015,
        color: '#fff'
      }
    },
      React.createElement('span', { style: { fontSize: 13, fontWeight: 500 } },
        selectedCount + ' selecionado' + (selectedCount > 1 ? 's' : '')
      ),
      React.createElement('div', { style: { width: 1, height: 20, background: 'rgba(255,255,255,0.2)' } }),
      React.createElement(Button, {
        type: 'danger', size: 'small', icon: 'delete',
        onClick: handleBulkDelete
      }, 'Excluir'),
      React.createElement('a', {
        style: { fontSize: 12, color: 'rgba(255,255,255,0.6)', cursor: 'pointer' },
        onClick: function() { setSelectedMap({}); }
      }, 'Limpar')
    )
  );
};
